<script setup lang="ts">
import ApexChart from 'apexcharts'

import { DoubleLineChart } from '@/components/charts/DoubleLineChart'

import useSessionStorage from '@/composables/sessionStorage'
const chartInstance = ref(null)
const chart = new DoubleLineChart()
const formattedData = ref([])
const dates = ref([])
const { t } = useI18n()
const { formatDate } = useDate()
const { dashboardItem } = useSessionStorage()
const { formatNumber } = useNumber()
const { tooltipAreaChartContent } = useDashboard()

const noData = computed(() => {
  return formattedData.value[0]?.data.length === 0 || !formattedData.value[0]?.data
})

const data = computed(() => {
  return dashboardItem.value?.inStore?.visitors
})

const initChart = () => {
  formatData()
  if (!noData.value) {
    const chartElement = createElement('visitors-per-day')
    chart.setColor(['#C4C4C4', '#5b21b6'])
    chart.setSeries(formattedData.value)
    chart.setStroke({
      curve: 'straight',
      dashArray: [8, 0],
      width: 2
    })
    setChartFormatters()
    chartInstance.value = new ApexChart(chartElement, chart.options)
    chartInstance.value.render()
  }
}

const setChartFormatters = () => {
  chart.options.tooltip.custom = ({ dataPointIndex, w }) => {
    const seriesData1 = w.globals.initialSeries[0]
    const seriesData2 = w.globals.initialSeries[1]
    const fullDatePreviousPeriod = formatDate({
      date: seriesData1.data[dataPointIndex].fullDate,
      toFormat: 'ddd DD MMM'
    })
    const fullDateCurrentPeriod = formatDate({
      date: seriesData2.data[dataPointIndex].fullDate,
      toFormat: 'ddd DD MMM'
    })
    const title = `${fullDatePreviousPeriod} vs ${fullDateCurrentPeriod}`
    const serie2Value = seriesData2.data[dataPointIndex].y
    const graph = t('pages.dashboard.dashboardPage.inStore.tooltip.visitors', serie2Value > 1 ? 2 : 1)
    const variation =
      seriesData1.data[dataPointIndex].y !== 0
        ? ((serie2Value - seriesData1.data[dataPointIndex].y) / seriesData1.data[dataPointIndex].y) * 100
        : 0
    return tooltipAreaChartContent(title, graph, formatNumber(serie2Value).toString(), variation)
  }
}

const createElement = (ref) => {
  let elem = document.getElementById(ref)
  let chartElement = document.createElement('div')
  chartElement = elem.appendChild(chartElement)
  chartElement.setAttribute('id', `${ref}-apex`)
  return chartElement
}
const formatData = () => {
  let formattedDatas = [
    {
      name: t('pages.dashboard.dashboardPage.inStore.previousPeriod'),
      data: []
    },
    {
      name: t('pages.dashboard.dashboardPage.inStore.currentPeriod'),
      data: []
    }
  ]
  if (!data.value) {
    return
  }

  data.value.visitorsPerDayCurrentPeriod.map((c) => {
    formattedDatas[1].data.push({
      x: new Date(c.checkinDate).getTime(),
      y: c.count,
      fullDate: c.checkinDate
    })
  })
  data.value.visitorsPerDayPreviousPeriod.map((d) => {
    formattedDatas[0].data.push({
      y: d.count,
      previousDate: d.checkinDate,
      fullDate: d.checkinDate
    })
  })
  // Sort data by date
  formattedDatas[1].data.sort((a, b) => (a.x > b.x ? 1 : b.x > a.x ? -1 : 0))
  formattedDatas[0].data.sort((a, b) =>
    a.previousDate > b.previousDate ? 1 : b.previousDate > a.previousDate ? -1 : 0
  )

  // Save current period dates
  formattedDatas[1].data.map((c) => dates.value.push(new Date(c.x).getTime()))

  formattedDatas.map((formattedData) => {
    formattedData.data.map((d, index) => {
      if (d.x) {
        d.x = new Date(d.x).getTime() // Format date
      }
      if (d.previousDate) {
        d.x = dates.value[index] // Get corresponding date from current period
        d.previousDate = formatDate({ date: d.previousDate, toFormat: 'DD MMM' })
      }
    })
  })

  formattedData.value = formattedDatas
}

onMounted(() => {
  initChart()
})

onBeforeUnmount(() => {
  if (chartInstance.value) {
    chartInstance.value.destroy()
  }
})
</script>
<template>
  <div id="visitors-per-day" class="w-full" />
  <TheNoResult
    v-if="noData"
    image="/images/no-results-analytics.png"
    :title="t('pages.review.reviewAnalyticsPage.chart.noResults.title')"
    :description="t('pages.review.reviewAnalyticsPage.chart.noResults.description')"
  />
</template>
